import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Music, Home as HomeIcon, Briefcase, PartyPopper, Gift, Shield, Flower2 } from "lucide-react";
import { SERVICES, PACKAGES, THERAPIST } from "@/data/catalog";
import { CHAIR_MASSAGE, HOME_SERVICE, ADDITIONAL_SERVICES } from "@/data/extras";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";
import ServicesSection from "@/components/ServicesSection";
import SEODirectory from "@/components/SEODirectory";

// Detailed menu — everything the curious visitor could want to know.
export default function Menu() {
    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        // Attend le rendu complet des cartes avant de scroller
        const id = hash.replace(/^#/, "");
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
        return () => clearTimeout(t);
    }, [hash]);

    useSEO({
        title: "Menu complet des soins · Jérémy Tessier — Massothérapie à Blainville",
        description: "Menu détaillé de tous les massages, tarifs, options à domicile, entreprise, événements, forfaits, cartes-cadeaux et sonothérapie. Cabinet à Blainville, service Rive-Nord.",
        canonical: "https://massoclic.ca/menu",
        ogImage: "https://massoclic.ca/icon.svg",
    });

    return (
        <main data-testid="menu-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                {/* Hero */}
                <div className="mb-14">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        Menu détaillé
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Toute l&apos;offre,{" "}
                        <span className="italic text-[#E07A5F]">en détail</span>.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-3xl">
                        Une lecture complète des soins, tarifs, formules à domicile, événements et sonothérapie. Pour les visiteurs curieux qui veulent tout comprendre avant de choisir.
                    </p>
                    <p className="mt-3 inline-flex items-center gap-2 text-sm text-[#3D8C95] italic font-medium">
                        <Shield size={16} /> Reçu d&apos;assurance fourni pour chaque soin.
                    </p>
                    <div className="mt-6">
                        <ReserveButton />
                    </div>
                </div>
            </div>

            {/* Aperçu visuel — cartes des soins (auparavant sur home) */}
            <ServicesSection />

            <div className="max-w-5xl mx-auto px-6 md:px-12">
                {/* 1. Massages en clinique — détails */}
                <section id="clinique" className="mb-16 scroll-mt-24">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">1.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">Massages en clinique</h2>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-6">
                        Au 1436 boulevard Curé-Labelle à Blainville · stationnement gratuit · réservation en ligne 24/7.
                    </p>
                    <div className="space-y-4">
                        {SERVICES.map((s) => (
                            <article
                                key={s.id}
                                id={`svc-${s.id}`}
                                data-testid={`menu-detail-${s.id}`}
                                className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-7 shadow-[0_8px_30px_rgba(44,42,41,0.04)] scroll-mt-32"
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                                    <div>
                                        <h3 className="font-display text-2xl text-[#2C2A29]">{s.name}</h3>
                                        <div className="text-sm text-[#3D8C95] italic">{s.subtitle}</div>
                                    </div>
                                    {s.tag && (
                                        <span className="text-[10px] uppercase tracking-widest bg-[#E07A5F]/10 text-[#E07A5F] rounded-full px-3 py-1">
                                            {s.tag}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-[#6E6862] leading-relaxed">{s.description}</p>
                                {s.details && (
                                    <div className="mt-4 grid md:grid-cols-2 gap-4">
                                        <div className="rounded-2xl bg-[#FAF7F2] p-4">
                                            <div className="text-[11px] uppercase tracking-widest text-[#3D8C95] mb-2 font-medium">
                                                Déroulement
                                            </div>
                                            <p className="text-sm text-[#2C2A29] leading-relaxed">
                                                {s.details.deroulement}
                                            </p>
                                        </div>
                                        <div className="rounded-2xl bg-[#FAF7F2] p-4">
                                            <div className="text-[11px] uppercase tracking-widest text-[#E07A5F] mb-2 font-medium">
                                                Bienfaits
                                            </div>
                                            <ul className="text-sm text-[#2C2A29] leading-relaxed space-y-1">
                                                {s.details.bienfaits.map((b, i) => (
                                                    <li key={i} className="flex gap-2">
                                                        <span className="text-[#E07A5F] shrink-0">·</span>
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-4 pt-4 border-t border-[#E8E2D9] flex flex-wrap gap-3">
                                    {s.variants.map((v) => (
                                        <span
                                            key={v.id}
                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#E8E2D9] text-sm"
                                        >
                                            <span className="text-[#2C2A29]">{v.label}</span>
                                            <span className="text-[#E07A5F] font-medium">{v.price} $</span>
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-4 pt-4 border-t border-[#E8E2D9]">
                                    <a
                                        href="/#reservation"
                                        data-testid={`menu-book-${s.id}`}
                                        className="block"
                                    >
                                        <Button
                                            className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6"
                                        >
                                            Réserver ce soin <ArrowRight size={16} className="ml-2" />
                                        </Button>
                                    </a>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* 2. Services supplémentaires — Yoga Thaï, en clinique */}
                <section id="supplementaires" className="mb-16 scroll-mt-24">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">2.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <Flower2 size={26} className="inline mr-2 -mt-1 text-[#E07A5F]" />
                            Services supplémentaires
                        </h2>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-6">
                        En marge de la carte principale, quelques soins spécialisés — disponibles exclusivement en clinique.
                    </p>
                    <div className="space-y-4">
                        {ADDITIONAL_SERVICES.map((s) => (
                            <article
                                key={s.id}
                                data-testid={`menu-detail-add-${s.id}`}
                                className="rounded-3xl border-2 border-[#E07A5F]/25 bg-white p-6 md:p-7 shadow-[0_8px_30px_rgba(44,42,41,0.04)]"
                            >
                                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                                    <div>
                                        <h3 className="font-display text-2xl text-[#2C2A29]">{s.name}</h3>
                                        <div className="text-sm text-[#3D8C95] italic">{s.subtitle}</div>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest bg-[#E07A5F]/10 text-[#E07A5F] rounded-full px-3 py-1">
                                        Nouveau
                                    </span>
                                </div>
                                <p className="text-sm text-[#6E6862] leading-relaxed">{s.description}</p>
                                <div className="mt-4 pt-4 border-t border-[#E8E2D9] flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#E8E2D9] text-sm">
                                        <span className="text-[#2C2A29]">{s.duration}</span>
                                        <span className="text-[#E07A5F] font-medium">{s.price} $</span>
                                    </span>
                                    <Link
                                        to={`/${s.slug}`}
                                        className="text-sm text-[#3D8C95] hover:text-[#2C6D75] inline-flex items-center gap-1 underline underline-offset-4"
                                    >
                                        En savoir plus <ArrowRight size={14} />
                                    </Link>
                                </div>
                                <div className="mt-4 pt-4 border-t border-[#E8E2D9]">
                                    <a
                                        href="/#reservation"
                                        data-testid={`menu-book-add-${s.id}`}
                                        className="block"
                                    >
                                        <Button
                                            className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6"
                                        >
                                            Réserver ce soin <ArrowRight size={16} className="ml-2" />
                                        </Button>
                                    </a>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* 3. Forfaits */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">3.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">Forfaits</h2>
                    </div>
                    <p className="text-sm text-[#6E6862] mb-6">
                        Économisez jusqu&apos;à 160 $. Valides 12 mois. Utilisables pour tous les types de massages, en clinique <b>ou à domicile sans supplément</b>.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {PACKAGES.map((p, i) => (
                            <div
                                key={p.id}
                                data-testid={`menu-pkg-${p.id}`}
                                className={`rounded-2xl p-5 border ${i === 2 ? "bg-[#2C2A29] text-white border-[#2C2A29]" : "bg-white border-[#E8E2D9]"}`}
                            >
                                <div className={`text-xs uppercase tracking-widest mb-1 ${i === 2 ? "text-[#E07A5F]" : "text-[#3D8C95]"}`}>Forfait</div>
                                <div className="font-display text-4xl">{p.count}</div>
                                <div className={`text-xs ${i === 2 ? "text-white/70" : "text-[#6E6862]"}`}>massages</div>
                                <div className={`mt-3 pt-3 border-t ${i === 2 ? "border-white/20" : "border-[#E8E2D9]"}`}>
                                    <div className="font-display text-2xl">{p.price} <span className="text-sm">$</span></div>
                                    <div className="text-xs text-[#3D8C95]">Économie {p.save} $</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 4. À domicile */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">4.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <HomeIcon size={26} className="inline mr-2 -mt-1 text-[#E07A5F]" />
                            Massage à domicile
                        </h2>
                    </div>
                    <div className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-7">
                        <p className="text-sm text-[#6E6862] leading-relaxed">
                            Je me déplace dans un rayon de <b>{HOME_SERVICE.radiusKm} km autour de Blainville</b> avec table
                            professionnelle, huiles biologiques, serviettes et musique douce.
                        </p>
                        <ul className="mt-4 space-y-2 text-sm text-[#2C2A29]">
                            <li>• <b>À l&apos;unité</b> : tarif clinique + {HOME_SERVICE.surcharge} $ de déplacement</li>
                            <li>• <b>Forfaits</b> : même prix qu&apos;en clinique — le déplacement est offert</li>
                            <li>• Réservation par courriel ou téléphone en précisant l&apos;adresse</li>
                        </ul>
                        <Link to="/massage-domicile" className="mt-4 inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                            Détails et tarifs complets <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>

                {/* 5. Entreprise */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">5.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <Briefcase size={26} className="inline mr-2 -mt-1 text-[#3D8C95]" />
                            Massage entreprise sur chaise
                        </h2>
                    </div>
                    <div className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-7">
                        <p className="text-sm text-[#6E6862] leading-relaxed">
                            Chair massage professionnel dans votre entreprise. <b>{CHAIR_MASSAGE.hourlyRate} $ / heure</b>,
                            fractionnable selon vos besoins.
                        </p>
                        {CHAIR_MASSAGE.details && (
                            <div className="mt-4 grid md:grid-cols-2 gap-4">
                                <div className="rounded-2xl bg-[#FAF7F2] p-4">
                                    <div className="text-[11px] uppercase tracking-widest text-[#3D8C95] mb-2 font-medium">
                                        Déroulement
                                    </div>
                                    <p className="text-sm text-[#2C2A29] leading-relaxed">
                                        {CHAIR_MASSAGE.details.deroulement}
                                    </p>
                                </div>
                                <div className="rounded-2xl bg-[#FAF7F2] p-4">
                                    <div className="text-[11px] uppercase tracking-widest text-[#E07A5F] mb-2 font-medium">
                                        Bienfaits
                                    </div>
                                    <ul className="text-sm text-[#2C2A29] leading-relaxed space-y-1">
                                        {CHAIR_MASSAGE.details.bienfaits.map((b, i) => (
                                            <li key={i} className="flex gap-2">
                                                <span className="text-[#E07A5F] shrink-0">·</span>
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                            {CHAIR_MASSAGE.options.map((o) => (
                                <span key={o.id} className="text-xs px-3 py-1.5 rounded-full bg-[#FAF7F2] border border-[#E8E2D9]">
                                    {o.duration} min · <span className="text-[#3D8C95] font-medium">{o.price} $</span>
                                </span>
                            ))}
                        </div>
                        <Link to="/massage-entreprise" className="mt-4 inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                            Détails et devis <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>

                {/* 6. Événements */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">6.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <PartyPopper size={26} className="inline mr-2 -mt-1 text-[#E07A5F]" />
                            Événements privés
                        </h2>
                    </div>
                    <div className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-7">
                        <p className="text-sm text-[#6E6862] leading-relaxed">
                            Mariages, anniversaires, retraites bien-être, journées RH, baby-showers, événements sportifs.
                            <b> Mêmes tarifs que le corporate</b> — {CHAIR_MASSAGE.hourlyRate} $ / heure.
                        </p>
                        <Link to="/evenements" className="mt-4 inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                            Détails et occasions <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>

                {/* 7. Cartes-cadeaux */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">7.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <Gift size={26} className="inline mr-2 -mt-1 text-[#C44569]" />
                            Cartes-cadeaux
                        </h2>
                    </div>
                    <div className="rounded-3xl border border-[#E8E2D9] bg-white p-6 md:p-7">
                        <p className="text-sm text-[#6E6862] leading-relaxed">
                            De 80 $ à 180 $, personnalisables, valides 12 mois. Utilisables en clinique ou à domicile
                            (+20 $ à l&apos;unité, offert pour les forfaits).
                        </p>
                        <Link to="/cartes-cadeaux" className="mt-4 inline-flex items-center gap-2 text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                            Voir les formules <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>

                {/* 8. Sonothérapie */}
                <section className="mb-16">
                    <div className="flex items-baseline gap-3 mb-6">
                        <span className="text-2xl font-display text-[#E07A5F]">8.</span>
                        <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29]">
                            <Music size={26} className="inline mr-2 -mt-1 text-[#3D8C95]" />
                            Sonothérapie & musicothérapie
                        </h2>
                    </div>
                    <div className="rounded-3xl bg-gradient-to-br from-[#3D8C95] to-[#2C6D75] text-white p-6 md:p-8">
                        <p className="text-sm leading-relaxed">
                            Kalimba, tongue drum, carillon — les paysages sonores font partie intégrante des soins Signature et Enrichi.
                            Les vibrations traversent le corps et apaisent le système nerveux, permettant une détente plus profonde
                            qu&apos;un massage seul. Sur demande, des séances dédiées à la sonothérapie peuvent être organisées.
                        </p>
                        <Link to="/exercices#musicotherapie" className="mt-4 inline-flex items-center gap-2 text-sm text-white/95 hover:text-white">
                            Écouter les instruments <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>

                {/* CTA final */}
                <div className="rounded-3xl bg-[#2C2A29] text-white p-8 md:p-10 text-center">
                    <h2 className="font-display text-3xl md:text-4xl">Prêt à choisir votre soin ?</h2>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                        <a href="/#reservation">
                            <Button
                                data-testid="menu-cta-book"
                                className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6"
                            >
                                Réserver en ligne
                            </Button>
                        </a>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6">
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>
            </div>
            <SEODirectory compact />
        </main>
    );
}
